import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { BuyListOptionList } from './BuyListOptionList';
import { sortBuyListsForLocation } from './addToBuyListTypes';
import type { BuyListTarget } from './useMyOpenBuyLists';

type AddToBuyListChooseStepProps = {
  currentLocationId: string;
  openBuyLists: BuyListTarget[];
  totalOpenBuyLists: number;
  isLoadingFirstPage: boolean;
  isLoadingMoreBuyLists: boolean;
  canLoadMoreBuyLists: boolean;
  hasBuyListsFailed: boolean;
  areBuyListsReadable: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onCreateRequested: () => void;
  onSelect: (buyList: BuyListTarget) => void;
};

const OPEN_LISTS_HEADING_ID = 'add-to-buy-list-open-lists-heading';

export function AddToBuyListChooseStep({
  currentLocationId,
  openBuyLists,
  totalOpenBuyLists,
  isLoadingFirstPage,
  isLoadingMoreBuyLists,
  canLoadMoreBuyLists,
  hasBuyListsFailed,
  areBuyListsReadable,
  onLoadMore,
  onRetry,
  onCreateRequested,
  onSelect,
}: Readonly<AddToBuyListChooseStepProps>) {
  const { t } = useTranslation('inventory');
  const sortedBuyLists = sortBuyListsForLocation(
    openBuyLists,
    currentLocationId
  );

  return (
    <section
      className="space-y-4"
      data-testid="add-to-buy-list-choose-list-section"
    >
      {isLoadingFirstPage && (
        <p className="text-sm text-neut-sec">
          {t('inventory.buy_list.loading')}
        </p>
      )}

      {hasBuyListsFailed && (
        <RetryBanner
          message={t('inventory.buy_list.lists_load_failed')}
          retryLabel={t('inventory.retry')}
          onRetry={onRetry}
          testId="add-to-buy-list"
        />
      )}

      {areBuyListsReadable && (
        <>
          <h3 className="sr-only" id={OPEN_LISTS_HEADING_ID}>
            {t('inventory.buy_list.step_choose')}
          </h3>

          <BuyListOptionList
            buyLists={sortedBuyLists}
            testId="add-to-buy-list-open-lists"
            headingId={OPEN_LISTS_HEADING_ID}
            onSelect={onSelect}
          />

          {canLoadMoreBuyLists && (
            <div
              className="flex items-center gap-3 text-sm text-neut-sec"
              data-testid="add-to-buy-list-partial-notice"
            >
              {t('inventory.buy_list.partial_lists', {
                shown: openBuyLists.length,
                total: totalOpenBuyLists,
              })}
              <button
                type="button"
                className="px-2 py-1 border border-neut-300 rounded text-xs text-neut-700 hover:bg-neut-100 disabled:opacity-50"
                data-testid="add-to-buy-list-load-more"
                disabled={isLoadingMoreBuyLists}
                onClick={onLoadMore}
              >
                {isLoadingMoreBuyLists
                  ? t('inventory.buy_list.loading_more')
                  : t('inventory.buy_list.show_more_lists')}
              </button>
            </div>
          )}

          <Button
            variant="accent"
            onClick={onCreateRequested}
            data-testid="add-to-buy-list-create-button"
          >
            <Plus size={16} aria-hidden="true" />
            {t('inventory.buy_list.create_new')}
          </Button>
        </>
      )}
    </section>
  );
}
